import React from 'react';
import NoteRow from './NoteRow';
import LandedDrawer from './LandedDrawer';
import { groupNotesBySection } from '../../../../services/songSectionsModel';
import './NoteList.css';

/**
 * The note list for the current song: open notes (captured + focus) in the
 * order they were WRITTEN (2026-09-25), then a collapsed "Recently landed" drawer. Landed notes leave the
 * primary list so the working surface stays about what's still open.
 *
 * `teacherNames` maps authorTeacherId → display name for the meta line; the
 * current teacher's own just-jotted notes read "you".
 */
const NoteList = ({
	openNotes,
	landedNotes,
	teacherNames = {},
	currentTeacherId = null,
	onDeliver,
	onEdit = null, // (noteId, text) — useSongNotes.updateNoteText
	onDelete = null, // (noteId) — useSongNotes.deleteNote
	needsDeleteConfirm = null, // (noteId) => bool — delivered notes warn about the family
	sections = [], // the current song's sections, in song order (2026-09-25)
	onSetSection = null, // (noteId, section|null)
	emptyLabel = 'No notes yet — jot the first one below.',
}) => {
	const nameFor = (note) => {
		if (!note.authorTeacherId || note.authorTeacherId === currentTeacherId) return '';
		return teacherNames[note.authorTeacherId] || '';
	};

	const row = (note) => (
		<NoteRow
			key={note.id}
			note={note}
			teacherName={nameFor(note)}
			onDeliver={onDeliver}
			onEdit={onEdit}
			onDelete={onDelete}
			deleteWarnsFamily={needsDeleteConfirm ? needsDeleteConfirm(note.id) : false}
			sections={sections}
			onSetSection={onSetSection}
		/>
	);

	// Grouped by song section, in SONG order, once any note carries one; flat
	// (exactly as before) otherwise. Written order holds inside each group.
	const renderGroups = () => {
		const { grouped, groups } = groupNotesBySection(openNotes, sections);
		if (!grouped) return openNotes.map(row);
		return groups.map((g) => (
			<section key={g.key} className="ld-notegroup" aria-label={g.label || 'Whole song'}>
				<h4 className="ld-notegroup__label">{g.label || 'Whole song'}</h4>
				{g.notes.map(row)}
			</section>
		));
	};

	return (
		<div className="ld-notelist">
			{openNotes.length === 0 && landedNotes.length === 0 ? (
				<p className="ld-notelist__empty">{emptyLabel}</p>
			) : (
				renderGroups()
			)}

			{landedNotes.length > 0 && (
				<LandedDrawer
					notes={landedNotes}
					teacherNames={teacherNames}
					currentTeacherId={currentTeacherId}
					onEdit={onEdit}
					onDelete={onDelete}
					needsDeleteConfirm={needsDeleteConfirm}
					sections={sections}
					onSetSection={onSetSection}
				/>
			)}
		</div>
	);
};

export default NoteList;
